import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { FileSignature, Upload, Loader2, Save } from "lucide-react";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const toForm = (extract) => ({
  fields: { ...(extract?.fields || {}) },
  offerings: extract?.offerings || [],
  emails: (extract?.distribution_emails || []).join(", "),
});

const ExtractFields = ({ names, form, setForm, canEdit }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {names.map((n) => (
      <div key={n} className="space-y-2">
        <Label>{n}</Label>
        <Input value={form.fields[n] || ""} disabled={!canEdit} className="bg-secondary/50" data-testid={`subagr-field-${slug(n)}`}
          onChange={(e) => setForm((p) => ({ ...p, fields: { ...p.fields, [n]: e.target.value } }))} />
      </div>
    ))}
  </div>
);

const OfferingPicker = ({ offerings, selected, canEdit, onToggle }) => (
  <div className="space-y-2">
    <Label>Offering Subscribed</Label>
    <div className="flex flex-wrap gap-3">
      {offerings.map((o) => (
        <label key={o} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm" data-testid={`subagr-offering-${slug(o)}`}>
          <Checkbox checked={selected.includes(o)} disabled={!canEdit} onCheckedChange={() => onToggle(o)} /> {o}
        </label>
      ))}
    </div>
  </div>
);

export const SubAgreementExtractCard = ({ investorId, extract, fieldNames, offerings, canEdit, onUpdated }) => {
  const [form, setForm] = useState(toForm(extract));
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const key = JSON.stringify(extract || {});
  useEffect(() => { setForm(toForm(JSON.parse(key))); }, [key]);
  const names = Array.from(new Set([...fieldNames, ...Object.keys(extract?.fields || {})]));

  const upload = async (file) => {
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Only PDF files are supported"); return; }
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("field_names", JSON.stringify(fieldNames));
      fd.append("offerings", JSON.stringify(offerings));
      await API.post(`/investors/${investorId}/sub-agreement-extract`, fd);
      toast.success("Sub Agreement details extracted");
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to read the Sub Agreement");
    } finally {
      setParsing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await API.put(`/investors/${investorId}/sub-agreement-extract`, {
        fields: form.fields, offerings: form.offerings,
        distribution_emails: form.emails.split(/[,;\s]+/).filter(Boolean),
      });
      toast.success("Sub Agreement details updated");
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const toggleOffering = (o) => setForm((p) => ({ ...p, offerings: p.offerings.includes(o) ? p.offerings.filter((x) => x !== o) : [...p.offerings, o] }));

  return (
    <Card className="mt-4" data-testid="subagr-extract-card">
      <CardHeader className="p-6 pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-primary" /> Sub Agreement — Extracted Details
          </CardTitle>
          {canEdit && (
            <Button variant="secondary" size="sm" className="border border-border" disabled={parsing} onClick={() => fileRef.current?.click()} data-testid="subagr-upload-btn">
              {parsing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              {parsing ? "Processing..." : extract ? "Upload New File" : "Upload Sub Agreement"}
            </Button>
          )}
          <input ref={fileRef} type="file" accept=".pdf" className="hidden" data-testid="subagr-file-input"
            onChange={(e) => { if (e.target.files?.[0]) upload(e.target.files[0]); e.target.value = ""; }} />
        </div>
        <p className="text-xs text-muted-foreground">
          Stored separately — the investor's information above is not changed.
          {extract?.file_name && <> Source: <span className="text-foreground" data-testid="subagr-file-name">{extract.file_name}</span>{extract.parsed_at && ` · ${new Date(extract.parsed_at).toLocaleString()}`}</>}
        </p>
      </CardHeader>
      <CardContent className="p-6 pt-0 space-y-4">
        {parsing && <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="subagr-processing"><Loader2 className="w-4 h-4 animate-spin text-primary" /> Please wait, processing your document...</p>}
        {!extract && !parsing && <p className="text-sm text-muted-foreground" data-testid="subagr-empty">No Sub Agreement file uploaded yet.</p>}
        {extract && !parsing && (
          <>
            <ExtractFields names={names} form={form} setForm={setForm} canEdit={canEdit} />
            {offerings.length > 0 && <OfferingPicker offerings={offerings} selected={form.offerings} canEdit={canEdit} onToggle={toggleOffering} />}
            <div className="space-y-2">
              <Label>Distribution Emails</Label>
              <Input value={form.emails} disabled={!canEdit} className="bg-secondary/50" placeholder="comma separated" data-testid="subagr-distribution-emails"
                onChange={(e) => setForm((p) => ({ ...p, emails: e.target.value }))} />
            </div>
            {canEdit && (
              <div className="flex justify-end">
                <Button onClick={save} disabled={saving} data-testid="subagr-save-btn"><Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Changes"}</Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
